import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import { CloudUpload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { Attachment, Client, attachmentsApi, publishApi } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import { RESUME_FILE_ACCEPT, isResumeLive, resumeFileError } from '../../utils/resumeFile'
import ResumeFilePreview from './ResumeFilePreview'

interface UploadedResumePanelProps {
  client: Client
  onUpdate: (client: Partial<Client>) => void
}

const LOCKED_HINT = 'Unpublish the resume to change the file'

// The staff side of an uploaded resume: one designated file that becomes the
// client's resume when published. The file is uploaded as a draft, and the
// client's own record points at it; publishing, approval and unpublishing are
// the same ones the AI resume uses, in the tab header above.
export default function UploadedResumePanel({ client, onUpdate }: UploadedResumePanelProps) {
  const [file, setFileState] = useState<Attachment | null>(null)
  // What the panel is already showing, so reloading on a changed designation can
  // skip a file this panel just uploaded itself.
  const shownFileRef = useRef<Attachment | null>(null)
  const setFile = useCallback((next: Attachment | null) => {
    shownFileRef.current = next
    setFileState(next)
  }, [])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<'uploading' | 'removing' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { showSuccess, showError } = useNotification()
  const designatedId = client.resumeAttachmentId
  const locked = isResumeLive(client)

  const loadFile = useCallback(async () => {
    if (!designatedId) {
      setFile(null)
      setLoading(false)
      return
    }
    // An upload this panel just made is already in hand; asking again would flash
    // the skeleton over a file we are showing.
    if (shownFileRef.current?.id === designatedId) {
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      setError(null)
      const attachments = await attachmentsApi.list(client.id, 'resume_document')
      setFile(attachments.find((a) => a.id === designatedId && a.isActive !== false) ?? null)
    } catch (err) {
      console.error('Failed to load the uploaded resume:', err)
      setError("Couldn't load the resume file. Try refreshing the page.")
    } finally {
      setLoading(false)
    }
  }, [client.id, designatedId, setFile])

  useEffect(() => {
    void loadFile()
  }, [loadFile])

  const upload = async (chosen: File) => {
    const problem = resumeFileError(chosen)
    if (problem) {
      showError(problem)
      return
    }

    setBusy('uploading')
    setError(null)
    const previous = file
    let uploaded: Attachment | null = null
    try {
      // A draft, so the client cannot see it before the resume is published.
      uploaded = await attachmentsApi.upload(chosen, 'resume_document', client.id, undefined, true)
      const result = await publishApi.setResumeSource(client.id, 'uploaded', uploaded.id)
      setFile(uploaded)
      onUpdate({
        resumeSource: 'uploaded',
        resumeAttachmentId: uploaded.id,
        resumeIsDraft: true,
        resumeStatus: result.resumeStatus,
      })
      // The replaced file is no longer the resume; keep it out of the documents list.
      if (previous) {
        attachmentsApi
          .delete(previous.id)
          .catch((err) => console.error('Could not remove the replaced resume file:', err))
      }
      showSuccess(previous ? 'Resume file replaced' : 'Resume file uploaded')
    } catch (err) {
      console.error('Failed to upload the resume file:', err)
      // The file reached storage but was never made the resume: do not leave it
      // behind as a stray document.
      if (uploaded) {
        attachmentsApi
          .delete(uploaded.id)
          .catch((cleanupErr) => console.error('Could not remove the unused upload:', cleanupErr))
      }
      setError(err instanceof Error ? err.message : 'The resume file did not upload. Try again.')
    } finally {
      setBusy(null)
    }
  }

  const handleRemove = async () => {
    if (!file) return
    setConfirmRemove(false)
    setBusy('removing')
    setError(null)
    try {
      // The client record stops pointing at the file before the file goes.
      await publishApi.setResumeSource(client.id, 'uploaded')
      await attachmentsApi.delete(file.id)
      setFile(null)
      onUpdate({ resumeAttachmentId: undefined, resumeIsDraft: true, resumeStatus: 'not_started' })
      showSuccess('Resume file removed')
    } catch (err) {
      console.error('Failed to remove the resume file:', err)
      setError(err instanceof Error ? err.message : 'The resume file was not removed. Try again.')
    } finally {
      setBusy(null)
    }
  }

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0]
    event.target.value = ''
    if (chosen) void upload(chosen)
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setDragOver(false)
    const dropped = event.dataTransfer.files?.[0]
    if (dropped) void upload(dropped)
  }

  if (loading) {
    return (
      <Box sx={{ py: 1 }}>
        <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="40%" height={24} />
      </Box>
    )
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <input ref={inputRef} type="file" hidden accept={RESUME_FILE_ACCEPT} onChange={handleSelect} />

      {busy === 'uploading' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">Uploading resume...</Typography>
        </Box>
      )}

      {busy !== 'uploading' && !file && (
        <Paper
          variant="outlined"
          role="button"
          tabIndex={0}
          aria-label="Upload the client's resume"
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            setDragOver(false)
          }}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              inputRef.current?.click()
            }
          }}
          sx={{
            p: 6,
            textAlign: 'center',
            cursor: 'pointer',
            borderStyle: 'dashed',
            borderWidth: 2,
            borderColor: dragOver ? 'primary.main' : 'divider',
            backgroundColor: dragOver ? 'action.hover' : 'transparent',
            transition: 'all 0.2s',
            '&:hover, &:focus-visible': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
          }}
        >
          <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Upload the client's resume
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Drag and drop a PDF or Word document here, or click to browse
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Maximum file size: 10MB. The client sees it once you publish.
          </Typography>
        </Paper>
      )}

      {busy !== 'uploading' && file && (
        <ResumeFilePreview
          attachment={file}
          actions={
            <>
              <Tooltip title={locked ? LOCKED_HINT : 'Replace the file'}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UploadIcon />}
                    disabled={locked || busy !== null}
                    onClick={() => inputRef.current?.click()}
                  >
                    Replace
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={locked ? LOCKED_HINT : 'Remove the file'}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={locked || busy !== null}
                    onClick={() => setConfirmRemove(true)}
                    aria-label="Remove resume file"
                  >
                    {busy === 'removing' ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </>
          }
        />
      )}

      <Dialog open={confirmRemove} onClose={() => setConfirmRemove(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove the resume file?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The client will have no resume until you upload another file. You can upload a replacement
            at any time while the resume is unpublished.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(false)} autoFocus>
            Keep file
          </Button>
          <Button onClick={handleRemove} color="error" variant="contained">
            Remove file
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
